import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { getReferralCandidates, selectSpecialist } from "../APIs/referralApi";

export default function ReferralSpecialistSelectionModal({
  referralId,
  onClose,
  onSelected,
}) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getReferralCandidates(referralId)
      .then((data) => setCandidates(Array.isArray(data) ? data : []))
      .catch(() => setError("Failed to load specialist options."))
      .finally(() => setLoading(false));
  }, [referralId]);

  const handleConfirm = async () => {
    if (!selected) return;
    setSubmitting(true);
    setError("");
    try {
      await selectSpecialist(referralId, selected);
      onSelected?.();
    } catch (err) {
      setError(
        err?.response?.data?.message || err.message || "Selection failed",
      );
      setSubmitting(false);
    }
  };

  const scoreColor = (score) => {
    if (score >= 80) return "bg-green-100 text-green-700";
    if (score >= 50) return "bg-yellow-100 text-yellow-700";
    return "bg-gray-100 text-gray-600";
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h2 className="text-lg font-semibold">Choose Your Specialist</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Your provider has identified these specialists for you.
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {loading && (
            <p className="text-sm text-gray-500 text-center py-4">
              Loading specialists...
            </p>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}

          {!loading && candidates.length === 0 && !error && (
            <p className="text-sm text-gray-500 text-center py-4">
              No specialists found at this time. Please contact your provider.
            </p>
          )}

          {candidates.map((c) => {
            const id = c.providerId;
            const isSelected = selected === id;
            return (
              <button
                key={id}
                onClick={() => setSelected(id)}
                className={`w-full text-left border rounded-lg p-4 transition ${
                  isSelected
                    ? "border-purple-500 bg-purple-50"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-semibold text-sm">
                      Dr. {c.firstName} {c.lastName}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {c.specialtyName ?? c.specialty}
                    </p>
                    {(c.city || c.state) && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        {[c.city, c.state].filter(Boolean).join(", ")}
                      </p>
                    )}
                    {c.insuranceMatch != null && (
                      <p
                        className={`text-xs mt-1 font-medium ${c.insuranceMatch ? "text-green-600" : "text-gray-400"}`}
                      >
                        {c.insuranceMatch
                          ? "✓ Accepts your insurance"
                          : "Insurance match unknown"}
                      </p>
                    )}
                  </div>
                  {c.score != null && (
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-bold ${scoreColor(c.score)}`}
                    >
                      {Math.round(c.score)}%
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded border"
          >
            Cancel
          </button>
          <button
            disabled={!selected || submitting}
            onClick={handleConfirm}
            className="px-4 py-2 text-sm rounded bg-purple-600 text-white disabled:opacity-40"
          >
            {submitting ? "Selecting..." : "Select This Specialist"}
          </button>
        </div>
      </div>
    </div>
  );
}
